import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { useAppForm } from 'shared/UI/UIKit/Form';
import {
  createScheduleConfiguration,
  getScheduleConfigurationById,
  getScheduleConstraints,
  updateScheduleConfiguration,
  type ScheduleConfiguration,
  type ScheduleConfigurationStatus,
  type ScheduleConstraint,
  type ScheduleConstraintWeight,
  ScheduleConstraintCode,
} from 'services/api/schedule-configuration';
import { useLazyGetMaxLessonsPerDayQuery } from 'shared/api';
import { getScheduleForWeekUrl } from 'services/navigation';
import type { EducationClassRow, ScheduleConfigurationFormValues } from '../types';
import type { MaybeType } from 'shared/types';
import {
  areAllConstraintsChecked,
  getCheckedConstraintsCount,
  getDefaultConstraints,
  setAllConstraintsMode,
  setConstraintMode,
} from '../utils';
import {
  mapEducationClassRowsToScheduleClasses,
  mapScheduleClassesToEducationClassRows,
} from '../utils/form-utils';

export const useScheduleConfiguration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const configurationIdParam = searchParams.get('configurationId');
  const isCopy = !!searchParams.get('copy');
  const configurationId = configurationIdParam ? Number(configurationIdParam) : null;

  const [form] = useAppForm();
  const [constraints, setConstraints] = useState<ScheduleConstraint[]>([]);
  const [educationClassRows, setEducationClassRows] = useState<EducationClassRow[]>([]);
  const [configurationStatus, setConfigurationStatus] =
    useState<ScheduleConfigurationStatus | null>(null);
  const [isEducationClassesTouched, setIsEducationClassesTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [allConstraints, setAllConstraints] = useState<ScheduleConstraint[]>([]);
  const [logicalConstraints, setLogicalConstraints] = useState<ScheduleConstraint[]>([]);
  const [constraintWeights, setConstraintWeights] = useState<ScheduleConstraintWeight[]>([]);
  const isEditMode = configurationId != null && !Number.isNaN(configurationId);
  const [fetchMaxLessonsPerDay, { data: maxLessonsConfigurations = [] }] =
    useLazyGetMaxLessonsPerDayQuery();
  const [maxLessonsPerDayId, setMaxLessonsPerDayId] = useState<MaybeType<number>>(null);
  const [constraintWeightsOpen, setConstraintWeightsOpen] = useState(false);

  const readOnly = configurationStatus != null && configurationStatus !== 'DRAFT' && !isCopy;

  const educationClasses = useMemo(
    () => mapEducationClassRowsToScheduleClasses(educationClassRows),
    [educationClassRows],
  );
  const educationClassesCount = educationClasses.length;
  const isEducationClassesInvalid = isEducationClassesTouched && educationClassesCount === 0;

  const allConstraintsChecked = useMemo(
    () => areAllConstraintsChecked(constraints, allConstraints),
    [constraints, allConstraints],
  );
  const checkedConstraintsCount = useMemo(
    () => getCheckedConstraintsCount(constraints),
    [constraints],
  );
  const indeterminate = constraints.some((c) => c.mode === 'ENABLED') && !allConstraintsChecked;
  const selectedMaxLessonsConfiguration = useMemo(
    () => maxLessonsConfigurations?.find((c) => c.id === maxLessonsPerDayId) ?? null,
    [maxLessonsConfigurations, maxLessonsPerDayId],
  );
  const sanpinWeightConstraints = useMemo(
    () =>
      allConstraints.map((constraint) => ({
        ...constraint,
        category: 'SANPIN' as const,
        mode: constraints.find((current) => current.code === constraint.code)?.mode ?? 'DISABLED',
      })),
    [allConstraints, constraints],
  );

  useEffect(() => {
    fetchMaxLessonsPerDay();
    getScheduleConstraints('SANPIN').then((res) => {
      setAllConstraints(res);
    });
    getScheduleConstraints('LOGICAL').then((res) => {
      setLogicalConstraints(res);
    });
    if (!isEditMode) {
      setInitialLoadDone(true);
      return;
    }
    setLoading(true);
    getScheduleConfigurationById(configurationId)
      .then((config) => {
        setConfigurationStatus(config.status ?? 'DRAFT');
        form.setFieldsValue({
          name: config.name,
          maxLessonsPerDayId: config?.maxLessonsPerDayId ?? 0,
          difficultyDistributionModelCode: config.difficultyDistributionModelCode,
        });
        setMaxLessonsPerDayId(config?.maxLessonsPerDayId ?? 0);
        setConstraints([
          ...(config.constraints?.filter((c) => c.category === 'SANPIN') ??
            getDefaultConstraints(allConstraints)),
        ]);
        setConstraintWeights(config.constraintWeights ?? []);
        setEducationClassRows(mapScheduleClassesToEducationClassRows(config.educationalClasses));
      })
      .catch(() => {
        notification.error({ message: 'Не удалось загрузить конфигурацию расписания' });
      })
      .finally(() => {
        setLoading(false);
        setInitialLoadDone(true);
      });
  }, [configurationId, isEditMode, form, fetchMaxLessonsPerDay]);

  const handleCheckAll = useCallback(
    (e: { target: { checked: boolean } }) => {
      setConstraints(
        setAllConstraintsMode(e.target.checked ? 'ENABLED' : 'DISABLED', allConstraints),
      );
    },
    [allConstraints],
  );

  const handleConstraintItemChange = useCallback(
    (code: ScheduleConstraintCode, checked: boolean) => {
      setConstraints((prev) =>
        setConstraintMode(prev, code, checked ? 'ENABLED' : 'DISABLED', allConstraints),
      );
    },
    [allConstraints],
  );

  const handleMaxLessonsPerDayChange = useCallback(
    (value: number) => {
      setMaxLessonsPerDayId(value);
      form.setFieldsValue({ maxLessonsPerDayId: value });
    },
    [form],
  );

  const submitToApi = useCallback(() => {
    form
      .validateFields()
      .then((values: ScheduleConfigurationFormValues) => {
        const educationalClasses = mapEducationClassRowsToScheduleClasses(educationClassRows);
        const id = configurationId && !isCopy ? configurationId : 0;
        const payload: ScheduleConfiguration = {
          id,
          name: values.name ?? '',
          maxLessonsPerDayId:
            values?.maxLessonsPerDayId === 0 ? undefined : values.maxLessonsPerDayId,
          useBaseMaxLessonPerDay: values?.maxLessonsPerDayId === 0 ? true : false,
          educationalClasses,
          constraints: constraints.filter(
            (c) => c.code !== ScheduleConstraintCode.DIFFICULTY_DISTRIBUTION_MODEL,
          ),
          constraintWeights: constraintWeights.filter((weight) => {
            if (weight.category === 'LOGICAL') return true;
            return constraints.some(
              (constraint) => constraint.code === weight.code && constraint.mode === 'ENABLED',
            );
          }),
          difficultyDistributionModelCode: values.difficultyDistributionModelCode,
        };

        const request =
          isEditMode && configurationId != null && !isCopy
            ? updateScheduleConfiguration(configurationId, payload)
            : createScheduleConfiguration(payload);

        request
          .then(() => {
            notification.success({
              message:
                isEditMode && configurationId != null && !isCopy
                  ? 'Конфигурация расписания обновлена'
                  : 'Конфигурация расписания создана',
            });
            navigate(getScheduleForWeekUrl());
          })
          .catch(() => {
            notification.error({
              message:
                isEditMode && configurationId != null && !isCopy
                  ? 'Не удалось обновить конфигурацию расписания'
                  : 'Не удалось создать конфигурацию расписания',
            });
          });
      })
      .catch(() => {
        notification.error({ message: 'Не удалось сохранить конфигурацию расписания' });
      });
  }, [
    form,
    educationClassRows,
    constraints,
    constraintWeights,
    configurationId,
    isEditMode,
    isCopy,
    navigate,
  ]);

  const handleSubmit = useCallback(() => {
    setIsEducationClassesTouched(true);
    if (educationClassesCount === 0) return;
    submitToApi();
  }, [educationClassesCount, submitToApi]);

  const handleOpenConstraintWeightModal = () => setConstraintWeightsOpen(true);
  const handleCloseConstraintWeightModal = () => setConstraintWeightsOpen(false);

  return {
    state: {
      form,
      readOnly,
      loading,
      initialLoadDone,
      constraints,
      educationClassRows,
      educationClassesCount,
      isEducationClassesInvalid,
      allConstraintsChecked,
      checkedConstraintsCount,
      indeterminate,
      allConstraints,
      logicalConstraints,
      sanpinWeightConstraints,
      constraintWeights,
      maxLessonsConfigurations,
      maxLessonsPerDayId,
      selectedMaxLessonsConfiguration,
      constraintWeightsOpen,
    },
    methods: {
      handleSubmit,
      setConstraints,
      setConstraintWeights,
      setEducationClassRows,
      handleCheckAll,
      handleConstraintItemChange,
      handleMaxLessonsPerDayChange,
      handleOpenConstraintWeightModal,
      handleCloseConstraintWeightModal,
    },
  };
};
